/**
 * Database Backup Manager Component
 * Safe read-only backup functionality for Supabase
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Database, 
  Download, 
  History, 
  Settings, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  FileText,
  Table,
  Trash2,
  RefreshCw,
  Info
} from 'lucide-react';
import databaseBackupService from '../../lib/databaseBackupService';

const BackupManager = ({ onClose, isOpen = true }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedFormat, setSelectedFormat] = useState('json');
  const [includeSchema, setIncludeSchema] = useState(true);
  const [selectedTables, setSelectedTables] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [databaseStats, setDatabaseStats] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Load available tables
      const tables = await databaseBackupService.getAvailableTables();
      setAvailableTables(tables);
      setSelectedTables(tables); // Select all by default
      
      // Load database stats
      const stats = await databaseBackupService.getDatabaseStats();
      setDatabaseStats(stats);
      
      // Load backup history
      const history = databaseBackupService.getBackupHistory();
      setBackupHistory(history);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setMessage({ type: 'error', text: 'Failed to load database information' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableToggle = (tableName) => {
    setSelectedTables(prev => 
      prev.includes(tableName) 
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  const handleSelectAll = () => {
    setSelectedTables(availableTables);
  };

  const handleSelectNone = () => {
    setSelectedTables([]);
  };

  const handleRefreshTables = async () => {
    try {
      setIsRefreshing(true);
      setMessage({ type: 'info', text: 'Refreshing table list...' });
      
      // Discover and refresh tables
      const discovery = await databaseBackupService.refreshTables();
      
      if (discovery.error) {
        setMessage({ type: 'error', text: `Refresh failed: ${discovery.error}` });
        return;
      }
      
      // Update available tables
      const tableNames = discovery.tables.map(t => t.name);
      setAvailableTables(tableNames);
      
      // Update selected tables to include newly discovered ones
      const newTables = tableNames.filter(name => !selectedTables.includes(name));
      if (newTables.length > 0) {
        setSelectedTables([...selectedTables, ...newTables]);
        setMessage({ 
          type: 'success', 
          text: `Refresh complete! Found ${discovery.total} tables (${newTables.length} new)` 
        });
      } else {
        setMessage({ 
          type: 'success', 
          text: `Refresh complete! Found ${discovery.total} tables` 
        });
      }
      
      // Update last refresh time
      setLastRefresh(new Date().toISOString());
      
      // Reload database stats
      const stats = await databaseBackupService.getDatabaseStats();
      setDatabaseStats(stats);
      
    } catch (error) {
      console.error('Error refreshing tables:', error);
      setMessage({ type: 'error', text: 'Failed to refresh tables' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleBackup = async () => {
    if (selectedTables.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one table to backup' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage({ type: '', text: '' });

      // Create backup data
      const backupData = {
        metadata: {
          timestamp: new Date().toISOString(),
          format: selectedFormat,
          version: '1.0',
          tables: selectedTables
        },
        schema: {},
        data: {}
      };

      // Export schema if requested
      if (includeSchema) {
        for (const tableName of selectedTables) {
          const schema = await databaseBackupService.getTableSchema(tableName);
          if (schema) {
            backupData.schema[tableName] = schema;
          }
        }
      }

      // Export data for selected tables
      let totalRecords = 0;
      for (const tableName of selectedTables) {
        try {
          const tableData = await databaseBackupService.exportTableData(tableName);
          backupData.data[tableName] = tableData;
          totalRecords += tableData.totalCount;
        } catch (error) {
          console.error(`Failed to export ${tableName}:`, error);
          backupData.data[tableName] = {
            error: error.message,
            data: [],
            totalCount: 0
          };
        }
      }

      // Generate filename and download
      const filename = databaseBackupService.generateFilename(selectedFormat, 'akiblackcar_backup');
      databaseBackupService.downloadBackup(backupData, filename, selectedFormat);

      // Save to history
      const backupInfo = {
        filename,
        format: selectedFormat,
        tableCount: selectedTables.length,
        totalRecords,
        size: JSON.stringify(backupData).length
      };
      
      databaseBackupService.saveBackupToHistory(backupInfo);
      
      // Update local state
      const history = databaseBackupService.getBackupHistory();
      setBackupHistory(history);

      setMessage({ 
        type: 'success', 
        text: `Backup completed successfully! Downloaded ${filename}` 
      });

    } catch (error) {
      console.error('Error creating backup:', error);
      setMessage({ type: 'error', text: 'Failed to create backup. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all backup history?')) {
      databaseBackupService.clearBackupHistory();
      setBackupHistory([]);
      setMessage({ type: 'success', text: 'Backup history cleared' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Database Backup Manager</h2>
              <p className="text-gray-400 text-sm">Safe read-only backup for Supabase</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning */}
          <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <h3 className="text-yellow-400 font-medium">Important Safety Notice</h3>
                <p className="text-yellow-200 text-sm mt-1">
                  This backup system is <strong>READ-ONLY</strong> and will never modify or delete your database. 
                  All operations are safe and designed for free Supabase accounts without automated backups.
                </p>
              </div>
            </div>
          </div>

          {/* Database Stats */}
          {databaseStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Table className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-300 text-sm">Total Tables</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{databaseStats.totalTables}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-green-400" />
                  <span className="text-gray-300 text-sm">Total Records</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{databaseStats.totalRecords.toLocaleString()}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-gray-300 text-sm">Status</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">Ready</p>
              </div>
            </div>
          )}

          {/* Message */}
          {message.text && (
            <div className={`p-4 rounded-lg flex items-center space-x-2 ${
              message.type === 'success' 
                ? 'bg-green-900/20 border border-green-700 text-green-400'
                : 'bg-red-900/20 border border-red-700 text-red-400'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Backup Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span>Backup Configuration</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Export Format
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="json">JSON (Recommended)</option>
                  <option value="csv">CSV (Spreadsheet)</option>
                  <option value="sql">SQL (Database)</option>
                </select>
              </div>

              {/* Schema Option */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeSchema"
                  checked={includeSchema}
                  onChange={(e) => setIncludeSchema(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeSchema" className="text-gray-300">
                  Include table schema
                </label>
              </div>
            </div>

            {/* Advanced Options */}
            <div className="space-y-3">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Advanced Options</span>
                <svg 
                  className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4"
                  >
                    {/* Table Selection */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-300">
                          Select Tables to Backup
                        </label>
                          <button
                            onClick={handleRefreshTables}
                            disabled={isRefreshing}
                            className="flex items-center space-x-1 text-xs text-green-400 hover:text-green-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                          >
                            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                          </button>
                        </div>
                        <div className="space-x-2">
                          <button
                            onClick={handleSelectAll}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Select All
                          </button>
                          <button
                            onClick={handleSelectNone}
                            className="text-xs text-gray-400 hover:text-gray-300"
                          >
                            Select None
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                        {availableTables.map(tableName => (
                          <label key={tableName} className="flex items-center space-x-2 p-2 bg-gray-800 rounded hover:bg-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTables.includes(tableName)}
                              onChange={() => handleTableToggle(tableName)}
                              className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-300">{tableName}</span>
                            {databaseStats?.tableStats[tableName] && (
                              <span className="text-xs text-gray-500">
                                ({databaseStats.tableStats[tableName].rowCount})
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                      {lastRefresh && (
                        <div className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Last refreshed: {new Date(lastRefresh).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Backup History */}
          {backupHistory.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <History className="w-5 h-5" />
                  <span>Backup History</span>
                </h3>
                <button
                  onClick={handleClearHistory}
                  className="text-red-400 hover:text-red-300 text-sm flex items-center space-x-1"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear History</span>
                </button>
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {backupHistory.map(backup => (
                  <div key={backup.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-white text-sm font-medium">{backup.filename}</p>
                        <p className="text-gray-400 text-xs">
                          {formatDate(backup.timestamp)} • {backup.tableCount} tables • {backup.totalRecords} records
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-300 text-sm">{formatFileSize(backup.size)}</p>
                      <p className="text-gray-500 text-xs uppercase">{backup.format}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          <div className="text-xs text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            Safe read-only operations only
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleBackup}
              disabled={isLoading || selectedTables.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{isLoading ? 'Creating Backup...' : 'Create Backup'}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BackupManager;


